/**
 * Pantalla de inicio de sesión.
 */

import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { Boton, Campo, Etiquetado, Nota, Tarjeta } from "@/components/ui";
import { Logo } from "@/components/Logo";
import { mensajeDeError, SoftfiError } from "@/lib/api";
import { auth } from "@/lib/endpoints";
import { useAuth } from "@/stores/auth";

export function EntrarPage() {
  const navegar = useNavigate();
  const ubicacion = useLocation();
  const aplicarSesion = useAuth((s) => s.aplicarSesion);

  const [correo, setCorreo] = useState("");
  const [clave, setClave] = useState("");
  const [verClave, setVerClave] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      const sesion = await auth.entrar(correo.trim().toLowerCase(), clave);
      aplicarSesion(sesion);

      // Si el usuario pertenece a varios negocios, `tenant` viene vacío y hay
      // que elegir. Si no, entra directo a donde iba.
      if (!sesion.tenant) {
        navegar("/seleccionar-negocio", { replace: true });
        return;
      }

      const destino = (ubicacion.state as { desde?: string } | null)?.desde ?? "/";
      navegar(destino, { replace: true });
    } catch (err) {
      setError(mensajeDeError(err));
      // Vaciamos la contraseña pero dejamos el correo: lo más probable es que
      // el error fuera un dedazo en la clave.
      if (err instanceof SoftfiError && err.code === "invalid_credentials") setClave("");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div>
      {/*
        El logo grande, encima del titular.

        En la barra de arriba ya hay uno pequeño, pero ahí es decoración de la
        página; aquí es lo primero que se mira. Alguien que abre Softfi por la
        mañana en la tablet del mostrador tiene que ver de un golpe que está
        donde tiene que estar, antes de leer nada.
      */}
      <div className="mb-7 text-center">
        <div className="mb-5 flex justify-center">
          <Logo tamano={72} />
        </div>
        <h1 className="titulo-pagina">Entra a tu negocio</h1>
        <p className="mt-2 text-sm text-muted">
          Escribe tu correo y tu contraseña para continuar.
        </p>
      </div>

      <Tarjeta className="sm:p-6">
        <form onSubmit={enviar} className="space-y-4" noValidate>
          <Etiquetado label="Correo electrónico" requerido>
            <Campo
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              placeholder="tucorreo@ejemplo.com"
              autoComplete="email"
              // `inputMode` hace que el teclado del celular muestre la @ directamente.
              inputMode="email"
              autoCapitalize="off"
              autoFocus
              required
              error={Boolean(error)}
            />
          </Etiquetado>

          <Etiquetado label="Contraseña" requerido>
            <div className="relative">
              <Campo
                type={verClave ? "text" : "password"}
                value={clave}
                onChange={(e) => setClave(e.target.value)}
                placeholder="Tu contraseña"
                autoComplete="current-password"
                required
                error={Boolean(error)}
                className="pr-12"
              />
              <button
                type="button"
                onClick={() => setVerClave((v) => !v)}
                aria-label={verClave ? "Ocultar contraseña" : "Mostrar contraseña"}
                className="absolute right-1 top-1 rounded-lg p-2.5 text-muted transition-colors hover:text-ink"
              >
                {verClave ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </Etiquetado>

          {/*
            El enlace va DEBAJO del campo de contraseña, no al final del
            formulario: es el momento exacto en que alguien se da cuenta de que
            no la recuerda. Ponerlo abajo del todo obliga a buscarlo.
          */}
          <div className="-mt-1 text-right">
            <Link
              to="/recuperar"
              className="text-sm text-muted transition-colors hover:text-accent"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          {error && <Nota tono="peligro">{error}</Nota>}

          <Boton type="submit" ancho tamano="lg" cargando={enviando} icono={<LogIn size={18} />}>
            Entrar
          </Boton>
        </form>
      </Tarjeta>

      <p className="mt-6 text-center text-sm text-muted">
        ¿Todavía no tienes cuenta?{" "}
        <Link to="/registro" className="font-medium text-accent hover:underline">
          Registra tu negocio gratis
        </Link>
      </p>
    </div>
  );
}
